'use client'

import { Box, Table, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { SectionErrorBoundary } from '@/components/error/boundary'
import { useExchangeRates } from '@/services/currency'
import { CoinOverall } from '@/types/coin'
import { AllAvailableCurrencyCodes } from '@/types/currency'
import { currencySymbolMap } from '@/utils/number/currencyFormatter'
import { formatDecimal, formatEffectiveNumber, formatPercentageColor } from '@/utils/number/format'

import { PriceHistoryResult } from './PriceHistoryData'

interface PriceHistoryTableProps {
  coin: CoinOverall
  priceHistory: PriceHistoryResult
  currency?: AllAvailableCurrencyCodes
}

interface TableRow {
  time: string
  price: string
  change: string | { valueDiff: string; percentage: string }
  changeColor: string
}

function PriceHistoryTable({ coin, priceHistory, currency = 'USD' }: PriceHistoryTableProps) {
  const t = useTranslations('converter')
  const tPriceHistory = useTranslations('priceHistory')
  const tCommon = useTranslations('common')
  const { data: exchangeRates } = useExchangeRates()

  const fiatRate = useMemo(() => {
    if (!exchangeRates || currency === 'USD') return 1
    const rate = exchangeRates[currency]
    return rate ? Number(rate) : 1
  }, [exchangeRates, currency])
  const currentPrice = coin.usd_price || 0
  const currencySymbol = currencySymbolMap[currency] || '$'

  const formatValue = (value: number) => {
    return value < 0.01 ? formatEffectiveNumber(value, 2) : formatDecimal(value, 2)
  }

  const tableData: TableRow[] = useMemo(() => {
    const formatPrice = (price: number) => {
      if (price === 0) return '-'
      return `${currencySymbol}${formatValue(price * fiatRate)}`
    }

    const formatChange = (historicalPrice: number, priceChange: number) => {
      if (priceChange === 0 || historicalPrice === 0) return '-'

      const priceDiff = (currentPrice - historicalPrice) * fiatRate
      const sign = priceDiff > 0 ? '+' : '-'

      return {
        valueDiff: `${sign}${currencySymbol}${formatValue(Math.abs(priceDiff))}`,
        percentage: `(${(Math.abs(priceChange) * 100).toFixed(2)}%)`,
      }
    }

    return [
      {
        time: t('today'),
        price: formatPrice(currentPrice),
        change: '0.00%',
        changeColor: 'var(--content-secondary)',
      },
      {
        time: t('1Day'),
        price: formatPrice(priceHistory.day.price),
        change: formatChange(priceHistory.day.price, priceHistory.day.priceChange),
        changeColor: formatPercentageColor(priceHistory.day.priceChange),
      },
      {
        time: t('1Week'),
        price: formatPrice(priceHistory.week.price),
        change: formatChange(priceHistory.week.price, priceHistory.week.priceChange),
        changeColor: formatPercentageColor(priceHistory.week.priceChange),
      },
      {
        time: t('1Month'),
        price: formatPrice(priceHistory.month.price),
        change: formatChange(priceHistory.month.price, priceHistory.month.priceChange),
        changeColor: formatPercentageColor(priceHistory.month.priceChange),
      },
      {
        time: t('1Year'),
        price: formatPrice(priceHistory.year.price),
        change: formatChange(priceHistory.year.price, priceHistory.year.priceChange),
        changeColor: formatPercentageColor(priceHistory.year.priceChange),
      },
    ]
  }, [priceHistory, currentPrice, fiatRate, currencySymbol, t])

  return (
    <Box mt={{ base: 40, lg: 80 }}>
      <Text component="h2" fz={20} lh="28px" fw={600} mb={16}>
        {tPriceHistory('title', { token: coin.name })}
      </Text>

      <Table verticalSpacing={12} horizontalSpacing={24} withRowBorders layout="fixed">
        <Table.Thead>
          <Table.Tr c="var(--content-secondary)" lh="20px" fz={14} fw={600}>
            <Table.Th>{tCommon('time')}</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>{tCommon('price')}</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>{tCommon('change')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tableData.map((row, index) => (
            <Table.Tr key={index}>
              <Table.Td style={{ fontSize: '14px', fontWeight: 600 }}>{row.time}</Table.Td>
              <Table.Td
                fz={14}
                fw={600}
                style={{
                  textAlign: 'right',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.price}
              </Table.Td>
              <Table.Td
                fz={14}
                fw={600}
                c={row.changeColor}
                style={{
                  textAlign: 'right',
                }}
              >
                {typeof row.change === 'string' ? (
                  row.change
                ) : (
                  <Box
                    style={{
                      display: 'inline-block',
                      textAlign: 'right',
                      wordBreak: 'break-word',
                      maxWidth: '100%',
                    }}
                  >
                    <span>{row.change.valueDiff}</span>{' '}
                    <span style={{ whiteSpace: 'nowrap' }}>{row.change.percentage}</span>
                  </Box>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  )
}

export default SectionErrorBoundary.with(PriceHistoryTable)
